export type Verdict = "waste-present" | "no-waste" | "skip";

export interface DumpingSearchState {
  /** Total clips in the search window; fixed for the life of the search. */
  readonly count: number;
  readonly lo: number;
  readonly hi: number;
  readonly done: boolean;
  readonly history: ReadonlyArray<{ index: number; verdict: Verdict }>;
}

export function createDumpingSearchState(clipCount: number): DumpingSearchState {
  return { count: clipCount, lo: 0, hi: clipCount - 1, done: false, history: [] };
}

export function getMidpoint(state: DumpingSearchState): number {
  return Math.floor((state.lo + state.hi) / 2);
}

export function markWastePresent(state: DumpingSearchState): DumpingSearchState {
  const mid = getMidpoint(state);
  const hi = mid;
  const done = hi - state.lo <= 1;
  return {
    count: state.count,
    lo: state.lo,
    hi,
    done,
    history: [...state.history, { index: mid, verdict: "waste-present" }],
  };
}

export function markNoWaste(state: DumpingSearchState): DumpingSearchState {
  const mid = getMidpoint(state);
  const lo = mid;
  const done = state.hi - lo <= 1;
  return {
    count: state.count,
    lo,
    hi: state.hi,
    done,
    history: [...state.history, { index: mid, verdict: "no-waste" }],
  };
}

export function markSkip(state: DumpingSearchState): DumpingSearchState {
  const mid = getMidpoint(state);
  const hi = state.hi;
  const lo = state.lo + 1 < hi ? state.lo + 1 : state.lo;
  const done = hi - lo <= 1;
  return { count: state.count, lo, hi, done, history: [...state.history, { index: mid, verdict: "skip" }] };
}

const APPLY: Record<Verdict, (state: DumpingSearchState) => DumpingSearchState> = {
  "waste-present": markWastePresent,
  "no-waste": markNoWaste,
  skip: markSkip,
};

/**
 * Revert the most recent verdict. Because each mark is a pure function of the
 * prior state, undo simply replays every verdict except the last from a fresh
 * initial state — this reconstructs `lo`/`hi`/`done` exactly, with no separate
 * inverse logic to keep in sync. Undoing the initial (empty-history) state is a
 * no-op, and undoing a finished search clears `done` so it can be reopened.
 */
export function undo(state: DumpingSearchState): DumpingSearchState {
  if (state.history.length === 0) return state;
  const replay = state.history.slice(0, -1);
  return replay.reduce((acc, step) => APPLY[step.verdict](acc), createDumpingSearchState(state.count));
}
